import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Button,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Autocomplete
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  Visibility as VisibilityIcon,
  TouchApp as TouchAppIcon,
  Speed as SpeedIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  Timeline as TimelineIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import DateRangeSelector from '../../components/common/DateRangeSelector';
import { sortWebsitesByOrder, cleanUrl } from '../../utils/countryFlags';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

interface AnalyticsMetrics {
  totalVisitors: number;
  organicVisitors: number;
  totalSessions: number;
  bounceRate: number;
  avgSessionDuration: number;
  newUserPercentage: number;
  totalClicks: number;
  totalImpressions: number;
  avgCTR: number;
  avgPosition: number;
}

interface TrafficData {
  date: string;
  visitors: number;
  organicVisitors: number;
  sessions: number;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

interface TopPageData {
  url: string;
  pageTitle: string;
  visitors: number;
  organicVisitors: number;
  clicks: number;
  impressions: number;
  ctr: number;
  avgPosition: number;
  bounceRate: number;
}

interface TopQueryData {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  visitors: number;
}

const AnalyticsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState<'combined' | 'traffic' | 'search'>('combined');
  
  // Website selector and filter states (same pattern as other pages)
  const [websites, setWebsites] = useState<any[]>([]);
  const [selectedWebsiteId, setSelectedWebsiteId] = useState<string>('all');
  const [selectedContentType, setSelectedContentType] = useState<string>('all');
  
  // Filter states - default to month-to-date
  const getDefaultDateRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const today = String(now.getDate()).padStart(2, '0');
    
    return {
      fromDate: `${year}-${month}-01`,
      toDate: `${year}-${month}-${today}`
    };
  };
  
  const [dateFilter, setDateFilter] = useState(getDefaultDateRange());
  const [urlFilter, setUrlFilter] = useState<string>('');
  const [availableUrls, setAvailableUrls] = useState<string[]>([]);
  
  // Analytics data states
  const [metrics, setMetrics] = useState<AnalyticsMetrics>({
    totalVisitors: 0,
    organicVisitors: 0,
    totalSessions: 0,
    bounceRate: 0,
    avgSessionDuration: 0,
    newUserPercentage: 0,
    totalClicks: 0,
    totalImpressions: 0,
    avgCTR: 0,
    avgPosition: 0
  });
  
  const [trafficTrend, setTrafficTrend] = useState<TrafficData[]>([]);
  const [topPages, setTopPages] = useState<TopPageData[]>([]);
  const [topQueries, setTopQueries] = useState<TopQueryData[]>([]);

  const contentTypes = [
    { value: 'all', label: 'All Content' },
    { value: 'V', label: 'VIP' },
    { value: 'S', label: 'SEO' },
    { value: 'C', label: 'Commercial' },
    { value: 'I', label: 'Informational' },
    { value: 'IC', label: 'Info-Commercial' }
  ];

  // Fetch available websites (EXACT same pattern as Dashboard/Rankings)
  const fetchWebsites = async () => {
    try {
      const { getSupabaseClient } = await import('../../services/supabase');
      const supabaseAdmin = await getSupabaseClient(true);
      
      const { data: websiteData, error: websiteError } = await getSupabaseClient(true)
        .from('websites')
        .select('website_id, country, base_url')
        .neq('country', 'GLOBAL');
      
      if (websiteError) throw websiteError;
      
      if (websiteData) {
        // Sort websites using the standard order
        const sortedWebsites = sortWebsitesByOrder(websiteData);
        setWebsites(sortedWebsites);
        console.log('📊 Available websites for analytics:', sortedWebsites);
      }
    } catch (error) {
      console.error('Error fetching websites:', error);
    }
  };

  const handleWebsiteChange = (event: any) => {
    setSelectedWebsiteId(event.target.value);
  };

  const handleDateRangeChange = (dateRange: { fromDate: string; toDate: string }) => {
    setDateFilter(dateRange);
  };

  const handleClearFilters = () => {
    setSelectedContentType('all');
    setUrlFilter('');
  };
  
  // Fetch ALL URLs from the urls table for the selected website
  const fetchFilterOptions = async () => {
    try {
      const { getSupabaseClient } = await import('../../services/supabase');
      const supabaseAdmin = await getSupabaseClient(true);
      
      // Get ALL URLs from the urls table for this website
      let urlQuery = supabaseAdmin
        .from('urls')
        .select('url_id, full_url')
        .order('full_url');
        
      // Filter by website
      if (selectedWebsiteId !== 'all') {
        urlQuery = urlQuery.eq('website_id', selectedWebsiteId);
      }
      
      const { data: urlResult, error: urlError } = await urlQuery;
      
      if (urlError) {
        console.error('Error fetching URLs:', urlError);
      } else if (!urlResult || urlResult.length === 0) {
        setAvailableUrls([]);
      } else {
        // Extract ALL URLs from the urls table
        const urls = urlResult.map(item => item.full_url).filter(Boolean);
        setAvailableUrls(urls);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Fetch analytics data (inspired by OLD dashboard but using our new data sources)
  const fetchAnalyticsData = async () => {
    if (selectedWebsiteId === 'all') {
      // Reset all metrics when no website selected
      setMetrics({
        totalVisitors: 0,
        organicVisitors: 0,
        totalSessions: 0,
        bounceRate: 0,
        avgSessionDuration: 0,
        newUserPercentage: 0,
        totalClicks: 0,
        totalImpressions: 0,
        avgCTR: 0,
        avgPosition: 0
      });
      setTrafficTrend([]);
      setTopPages([]);
      setTopQueries([]);
      return;
    }

    // Make sure we have websites loaded
    if (websites.length === 0) {
      console.log('📊 No websites loaded yet, skipping analytics fetch');
      return;
    }

    setLoading(true);
    try {
      
      // Find the selected website
      const selectedWebsite = websites.find(w => w.website_id === selectedWebsiteId);
      if (!selectedWebsite) {
        console.error('📊 Selected website not found:', selectedWebsiteId, 'Available:', websites.map(w => w.website_id));
        throw new Error('Selected website not found');
      }

      console.log(`📊 === ANALYTICS DEBUG START ===`);
      console.log(`📊 Selected website:`, selectedWebsite);
      console.log(`📊 Website ID: "${selectedWebsiteId}"`);
      console.log(`📊 Date filter:`, dateFilter);
      console.log(`📊 Month filter: ${dateFilter.fromDate.substring(0, 7)} to ${dateFilter.toDate.substring(0, 7)}`);
      console.log(`📊 Content type:`, selectedContentType);
      console.log(`📊 URL filter:`, urlFilter);

      const { getSupabaseClient } = await import('../../services/supabase');
      const supabaseAdmin = await getSupabaseClient(true);
      
      // Get monthly analytics data from analytics_data table via API
      let analyticsData: any[] = [];
      let usingMonthlyData = false;
      let websiteUrls: any[] = [];
      
      // First try the analytics API (which uses monthly data)
      try {
        let apiUrl = `/api/analytics-daily/aggregated?websiteId=${selectedWebsiteId}&fromDate=${dateFilter.fromDate}&toDate=${dateFilter.toDate}`;
        
        // Add URL filter if specified
        if (urlFilter) {
          apiUrl += `&url=${encodeURIComponent(urlFilter)}`;
        }
        
        const aggregatedResponse = await fetch(apiUrl, {
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        if (aggregatedResponse.ok) {
          const aggregatedResult = await aggregatedResponse.json();
          console.log(`📊 Backend API response:`, aggregatedResult);
          
          // Check if we have actual data (not just zeros)
          // Check if we have actual data (not just zeros)
          const hasRealData = aggregatedResult.aggregated && 
              (aggregatedResult.aggregated.visitors_total > 0 || 
               aggregatedResult.aggregated.sessions_total > 0 ||
               aggregatedResult.aggregated.clicks > 0 ||
               aggregatedResult.aggregated.impressions > 0);
          
          if (hasRealData) {
            analyticsData = aggregatedResult.monthlyData || aggregatedResult.dailyData || [];
            usingMonthlyData = true;
            console.log(`📊 Using monthly analytics data: ${analyticsData.length} records`);
          } else {
            console.log(`📊 Analytics API returned all zeros, will try direct database query`);
          }
        }
      } catch (error) {
        console.log('📊 Analytics API error, will try direct database query:', error);
      }
      
      // If no API data, query analytics_data table directly
      if (!usingMonthlyData) {
        console.log('📊 Querying analytics_data table directly...');
        
        let analyticsQuery = supabaseAdmin
          .from('analytics_data')
          .select('visitors_total, sessions_total, bounce_rate_total, month, url_id, visitors_organic, new_users_total, new_users_organic, bounce_rate_organic, avg_session_duration_total, avg_session_duration_organic, pageviews_total, pageviews_organic')
          .gte('month', dateFilter.fromDate.substring(0, 7)) // YYYY-MM format
          .lte('month', dateFilter.toDate.substring(0, 7))
          .order('month', { ascending: false });

        // Apply website filter using URLs from the urls table
        const { data: urlsData } = await supabaseAdmin
          .from('urls')
          .select('url_id')
          .eq('website_id', selectedWebsiteId);
        
        if (urlsData && urlsData.length > 0) {
          websiteUrls = urlsData;
          const urlIds = websiteUrls.map(u => u.url_id);
          analyticsQuery = analyticsQuery.in('url_id', urlIds);
        } else {
          // Fallback to country pattern if no URLs found
          const websiteFilter = `${selectedWebsite.country}-%`;
          analyticsQuery = analyticsQuery.like('url_id', websiteFilter);
        }
        
        // Apply content type filter
        if (selectedContentType !== 'all') {
          analyticsQuery = analyticsQuery.like('url_id', `%-${selectedContentType}%`);
        }

        const { data: monthlyData, error: analyticsError } = await analyticsQuery.limit(1000) as any;

        if (analyticsError) {
          console.error('Analytics data error:', analyticsError);
          throw analyticsError;
        }

        analyticsData = monthlyData || [];
        console.log('📊 Analytics data fetched:', analyticsData?.length, 'records');
        console.log('📊 Sample analytics:', analyticsData?.slice(0, 3));
      }

      // Process analytics data based on the source
      if (analyticsData && analyticsData.length > 0) {
        console.log(`📊 Processing ${analyticsData.length} analytics records`);
        console.log(`📊 First record structure:`, Object.keys(analyticsData[0]));
        console.log(`📊 Using monthly data source`);
        
        // Get Search Console data for clicks/impressions
        let searchConsoleData: any[] = [];
        try {
          const searchQuery = supabaseAdmin
            .from('search_console_metrics')
            .select('*')
            .gte('date_retrieved', dateFilter.fromDate)
            .lte('date_retrieved', dateFilter.toDate);
            
          // Use the same URL IDs we got earlier
          if (websiteUrls && websiteUrls.length > 0) {
            const urlIds = websiteUrls.map(u => u.url_id);
            searchQuery.in('url_id', urlIds);
          } else {
            searchQuery.like('url_id', `${selectedWebsite.country}-%`);
          }
            
          const { data: searchData, error: searchError } = await searchQuery.limit(1000);
          if (!searchError && searchData) {
            searchConsoleData = searchData;
            console.log(`🔍 Found ${searchData.length} search console records`);
          }
        } catch (error) {
          console.log('📊 No search console data available');
        }
        
        // Get Search Console Keywords data for queries
        let keywordData: any[] = [];
        try {
          const keywordQuery = supabaseAdmin
            .from('search_console_keywords')
            .select('*')
            .gte('date_retrieved', dateFilter.fromDate)
            .lte('date_retrieved', dateFilter.toDate);
            
          // Use the same URL IDs we got earlier
          if (websiteUrls && websiteUrls.length > 0) {
            const urlIds = websiteUrls.map(u => u.url_id);
            keywordQuery.in('url_id', urlIds);
          } else {
            keywordQuery.like('url_id', `${selectedWebsite.country}-%`);
          }
            
          const { data: keywords, error: keywordError } = await keywordQuery.limit(1000);
          if (!keywordError && keywords) {
            keywordData = keywords;
            console.log(`🔍 Found ${keywords.length} search console keyword records`);
          }
        } catch (error) {
          console.log('📊 No search console keywords data available');
        }
        
        // Get URL details for better page titles
        let urlDetails: any[] = [];
        try {
          // Always use the website ID to determine the correct URLs to fetch
          const { data: urlData, error: urlError } = await supabaseAdmin
            .from('urls')
            .select('url_id, full_url, title, meta_title')
            .eq('website_id', selectedWebsiteId)
            .limit(1000);
          
          if (urlError) {
            console.error('📊 Error fetching URL details:', urlError.message);
          } else if (urlData) {
            urlDetails = urlData;
            console.log(`📊 Found ${urlData.length} URL records for website ${selectedWebsiteId}`);
            if (urlData.length > 0) {
              console.log('📊 Sample URL record:', urlData[0]);
            }
          }
        } catch (error) {
          console.error('📊 Exception fetching URL details:', error);
        }
        
        // Use the existing processAnalyticsData function
        await processAnalyticsData(analyticsData, searchConsoleData, selectedWebsite, urlDetails, keywordData);
      } else {
        console.log(`📊 No analytics data found for ${selectedWebsiteId} in date range: ${dateFilter.fromDate} to ${dateFilter.toDate}`);
        
        // Set empty state
        setMetrics({
          totalVisitors: 0,
          organicVisitors: 0,
          totalSessions: 0,
          bounceRate: 0,
          avgSessionDuration: 0,
          newUserPercentage: 0,
          totalClicks: 0,
          totalImpressions: 0,
          avgCTR: 0,
          avgPosition: 0
        });
        setTrafficTrend([]);
        setTopPages([]);
        setTopQueries([]);
      }

      return; // Exit early after processing analytics data

    } catch (error) {
      console.error('❌ Error fetching analytics data:', error);
      // Don't fall back to mock data - show error state
      setMetrics({
        totalVisitors: 0,
        organicVisitors: 0,
        totalSessions: 0,
        bounceRate: 0,
        avgSessionDuration: 0,
        newUserPercentage: 0,
        totalClicks: 0,
        totalImpressions: 0,
        avgCTR: 0,
        avgPosition: 0
      });
      setTrafficTrend([]);
      setTopPages([]);
      setTopQueries([]);
    } finally {
      setLoading(false);
    }
  };

  // Process analytics data using the correct field names from your table
  const processAnalyticsData = async (
    analyticsData: any[], 
    searchData: any[], 
    selectedWebsite: any,
    urlDetails: any[],
    keywordData: any[] = []
  ) => {
    console.log(`📊 Processing analytics data:`, analyticsData);
    console.log(`📊 Analytics data length:`, analyticsData.length);
    console.log(`📊 First 3 analytics records:`, analyticsData.slice(0, 3));
    console.log(`🔍 Processing search data:`, searchData);
    
    // Calculate overall metrics using the correct field names from your table
    const totalVisitors = analyticsData.reduce((sum, item) => sum + (item.visitors_total || 0), 0);
    const organicVisitors = analyticsData.reduce((sum, item) => sum + (item.visitors_organic || 0), 0);
    const totalSessions = analyticsData.reduce((sum, item) => sum + (item.sessions_total || 0), 0);
    const organicSessions = analyticsData.reduce((sum, item) => sum + (item.sessions_organic || 0), 0);
    const newUsersTotal = analyticsData.reduce((sum, item) => sum + (item.new_users_total || 0), 0);
    const newUsersOrganic = analyticsData.reduce((sum, item) => sum + (item.new_users_organic || 0), 0);
    
    // Calculate averages for bounce rate and session duration
    // Bounce rate needs to be multiplied by 100 to show as percentage
    const avgBounceRate = analyticsData.length > 0 
      ? (analyticsData.reduce((sum, item) => sum + (item.bounce_rate_total || 0), 0) / analyticsData.length) * 100
      : 0;
    const avgSessionDuration = analyticsData.length > 0 
      ? analyticsData.reduce((sum, item) => sum + (item.avg_session_duration_total || 0), 0) / analyticsData.length 
      : 0;
    
    // Search Console metrics - combine data from both search_console_metrics and search_console_keywords
    const totalClicks = searchData.reduce((sum, item) => sum + (item.clicks || 0), 0) +
                       keywordData.reduce((sum, item) => sum + (item.clicks || 0), 0);
    const totalImpressions = searchData.reduce((sum, item) => sum + (item.impressions || 0), 0) +
                            keywordData.reduce((sum, item) => sum + (item.impressions || 0), 0);
    
    const avgCTR = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    
    // Calculate average position from both sources
    const allPositions = [
      ...searchData.filter(item => item.position > 0).map(item => item.position),
      ...keywordData.filter(item => item.position > 0).map(item => item.position)
    ];
    const avgPosition = allPositions.length > 0 
      ? allPositions.reduce((sum, pos) => sum + pos, 0) / allPositions.length 
      : 0;

    // Calculate new user percentage
    const newUserPercentage = totalSessions > 0 ? (newUsersTotal / totalSessions) * 100 : 0;

    setMetrics({
      totalVisitors,
      organicVisitors,
      totalSessions,
      bounceRate: avgBounceRate,
      avgSessionDuration: avgSessionDuration,
      newUserPercentage,
      totalClicks,
      totalImpressions,
      avgCTR: Math.round(avgCTR * 10) / 10,
      avgPosition: Math.round(avgPosition * 10) / 10
    });

    // Create trend data (inspired by OLD trend analysis)
    const trendData = createTrendData(analyticsData, searchData);
    console.log(`📊 Setting traffic trend with ${trendData.length} data points`);
    setTrafficTrend(trendData);

    // Create top pages data (inspired by OLD URL analysis)
    const pagesData = createTopPagesData(analyticsData, searchData, urlDetails);
    console.log(`📊 Setting top pages with ${pagesData.length} pages`);
    setTopPages(pagesData);

    // Create top queries data from both search data and keyword data
    console.log('🔍 Search console metrics sample:', searchData.slice(0, 2));
    console.log('🔍 Search console metrics fields:', searchData.length > 0 ? Object.keys(searchData[0]) : 'No data');
    console.log('🔍 Search console keywords sample:', keywordData.slice(0, 2));
    console.log('🔍 Search console keywords fields:', keywordData.length > 0 ? Object.keys(keywordData[0]) : 'No data');
    const queriesData = createTopQueriesData(searchData, keywordData);
    console.log(`📊 Setting top queries with ${queriesData.length} queries`);
    setTopQueries(queriesData);
  };

  // Set mock data for demo (enhanced version of OLD demo data)
  // REMOVED: We no longer use mock data - only real data from Supabase
  /*
  const setMockAnalyticsData = () => {
    setMetrics({
      totalVisitors: 45892,
      organicVisitors: 29830,
      totalSessions: 38471,
      bounceRate: 42.8,
      avgSessionDuration: 205,
      newUserPercentage: 71.2,
      totalClicks: 12843,
      totalImpressions: 187650,
      avgCTR: 6.8,
      avgPosition: 8.4
    });

    // Generate mock trend data
    const mockTrend: TrafficData[] = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      mockTrend.push({
        date: date.toISOString().split('T')[0],
        visitors: Math.floor(Math.random() * 2000 + 1000),
        organicVisitors: Math.floor(Math.random() * 1300 + 650),
        sessions: Math.floor(Math.random() * 1800 + 900),
        clicks: Math.floor(Math.random() * 500 + 200),
        impressions: Math.floor(Math.random() * 8000 + 4000),
        ctr: Math.random() * 10 + 2,
        position: Math.random() * 15 + 5
      });
    }
    setTrafficTrend(mockTrend);

    // Mock top pages - showing OUR website pages only (not competitor data)
    setTopPages([
      {
        url: '/uk-travel-guide',
        pageTitle: 'Complete UK Travel Guide | Your Website',
        visitors: 8924,
        organicVisitors: 6847,
        clicks: 2156,
        impressions: 28904,
        ctr: 7.5,
        avgPosition: 3.2,
        bounceRate: 38.4
      },
      {
        url: '/london-attractions',
        pageTitle: 'Top London Attractions & Things to Do',
        visitors: 6731,
        organicVisitors: 5249,
        clicks: 1843,
        impressions: 22156,
        ctr: 8.3,
        avgPosition: 2.8,
        bounceRate: 35.1
      }
    ]);

    // Mock top queries - showing queries that bring traffic TO OUR website
    setTopQueries([
      {
        query: 'uk travel guide',
        clicks: 2156,
        impressions: 28904,
        ctr: 7.5,
        position: 3.2,
        visitors: 1924
      },
      {
        query: 'london attractions guide',
        clicks: 1843,
        impressions: 22156,
        ctr: 8.3,
        position: 2.8,
        visitors: 1673
      }
    ]);
  };
  */

  // Helper functions for data processing using correct field names
  const createTrendData = (analyticsData: any[], searchData: any[]): TrafficData[] => {
    // Create monthly data points for the chart
    const dateMap = new Map();
    
    // Group analytics data by month
    analyticsData.forEach(item => {
      const monthKey = item.month; // e.g., "2025-01"
      if (monthKey) {
        // Use the month as-is for monthly data
        if (!dateMap.has(monthKey)) {
          dateMap.set(monthKey, { 
            date: monthKey, 
            visitors: 0, 
            organicVisitors: 0,
            sessions: 0, 
            clicks: 0, 
            impressions: 0 
          });
        }
        const entry = dateMap.get(monthKey);
        entry.visitors += item.visitors_total || 0;
        entry.organicVisitors += item.visitors_organic || 0;
        entry.sessions += item.sessions_total || 0;
      }
    });

    // Process search data - convert daily dates to monthly format
    searchData.forEach(item => {
      let dateKey = item.date_retrieved || item.date || item.month;
      if (dateKey) {
        // Convert YYYY-MM-DD to YYYY-MM for monthly grouping
        if (dateKey.length > 7) {
          dateKey = dateKey.substring(0, 7);
        }
        
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, { 
            date: dateKey, 
            visitors: 0, 
            organicVisitors: 0,
            sessions: 0, 
            clicks: 0, 
            impressions: 0 
          });
        }
        const entry = dateMap.get(dateKey);
        entry.clicks += item.clicks || 0;
        entry.impressions += item.impressions || 0;
      }
    });

    const trendData = Array.from(dateMap.values())
      .sort((a, b) => a.date.localeCompare(b.date)) // Sort by date
      .map(item => ({
        ...item,
        ctr: item.impressions > 0 ? (item.clicks / item.impressions) * 100 : 0,
        position: 0 // Real positions come from search data
      }));
    
    console.log(`📈 Created trend data with ${trendData.length} points`);
    if (trendData.length > 0) {
      console.log(`📈 Sample trend point:`, trendData[0]);
    }
    
    return trendData;
  };

  const createTopPagesData = (analyticsData: any[], searchData: any[], urlDetails: any[]): TopPageData[] => {
    // Create URL lookup map
    const urlLookup = new Map();
    urlDetails.forEach(url => {
      // Map both url.id and url.url_id to handle different key formats
      if (url.id) urlLookup.set(url.id, url);
      if (url.url_id) urlLookup.set(url.url_id, url);
      // Also try with url_id field directly
      if (url.url_id) urlLookup.set(String(url.url_id), url);
    });
    
    console.log(`📊 URL lookup map created with ${urlLookup.size} entries`);
    console.log(`📊 URL details count: ${urlDetails.length}`);
    if (urlDetails.length > 0) {
      const firstUrl = urlDetails[0];
      console.log(`📊 Sample URL data:`, firstUrl);
    }
    if (analyticsData.length > 0) {
      console.log(`📊 Sample analytics url_id:`, analyticsData[0]?.url_id);
      console.log(`📊 Analytics url_id pattern:`, analyticsData.slice(0, 3).map(a => a.url_id));
    }

    // Group by url_id and aggregate metrics
    const urlMap = new Map();
    
    // Process analytics data
    analyticsData.forEach(item => {
      if (item.url_id && !urlMap.has(item.url_id)) {
        // Look up URL details
        const urlInfo = urlLookup.get(item.url_id);
        
        // Better fallback for page title and URL
        let pageTitle = 'Page';
        let pageUrl = '/';
        
        if (urlInfo) {
          pageTitle = urlInfo.title || urlInfo.meta_title || urlInfo.page_title || `Page ${item.url_id}`;
          pageUrl = urlInfo.full_url || urlInfo.url || urlInfo.path || '/';
        } else {
          // If no URL info found, try to make a readable title from url_id
          // Format: NL-HP000-IC-V -> Homepage, NL-01010-C-V -> Page 01010
          const parts = item.url_id.split('-');
          if (parts[1] === 'HP000' || parts[1] === 'HP001') {
            pageTitle = 'Homepage';
          } else if (parts[1]) {
            pageTitle = `Page ${parts[1]}`;
          }
        }
        
        urlMap.set(item.url_id, {
          url: pageUrl,
          pageTitle: pageTitle,
          visitors: 0,
          organicVisitors: 0,
          clicks: 0,
          impressions: 0,
          bounceRate: 0,
          bounceCount: 0,
          positions: []
        });
      }
      if (item.url_id) {
        const entry = urlMap.get(item.url_id);
        entry.visitors += item.visitors_total || 0;
        entry.organicVisitors += item.visitors_organic || 0;
        entry.bounceRate += item.bounce_rate_total || 0;
        entry.bounceCount += 1;
      }
    });

    // Add search console data - process URLs from search_console_metrics
    searchData.forEach(item => {
      // The search_console_metrics table has url_id field
      const itemUrlId = (item as any).url_id;
      if (itemUrlId) {
        // Try to find existing entry or create new one
        let matchedUrlId: string | null = null;
        let foundUrl = false;
        
        // First try to match with existing entries by url_id
        if (urlLookup.has(itemUrlId)) {
          matchedUrlId = itemUrlId;
          foundUrl = true;
        }
        
        // If no match found, create new entry with url_id as key
        if (!foundUrl) {
          const urlKey = itemUrlId;
          if (!urlMap.has(urlKey)) {
            const urlInfo = urlLookup.get(itemUrlId);
            
            // Better fallback for page title and URL
            let pageTitle = 'Page';
            let pageUrl = '/';
            
            if (urlInfo) {
              pageTitle = urlInfo.title || urlInfo.meta_title || urlInfo.page_title || `Page ${itemUrlId}`;
              pageUrl = urlInfo.full_url || urlInfo.url || urlInfo.path || '/';
            } else {
              // If no URL info found, try to make a readable title from url_id
              const parts = itemUrlId.split('-');
              if (parts[1] === 'HP000' || parts[1] === 'HP001') {
                pageTitle = 'Homepage';
              } else if (parts[1]) {
                pageTitle = `Page ${parts[1]}`;
              }
            }
            
            urlMap.set(urlKey, {
              url: pageUrl,
              pageTitle: pageTitle,
              visitors: 0,
              organicVisitors: 0,
              clicks: 0,
              impressions: 0,
              bounceRate: 0,
              bounceCount: 0,
              positions: []
            });
          }
          matchedUrlId = urlKey;
        }
        
        if (matchedUrlId && urlMap.has(matchedUrlId)) {
          const entry = urlMap.get(matchedUrlId);
          entry.clicks += item.clicks || 0;
          entry.impressions += item.impressions || 0;
          if (item.position) {
            entry.positions = entry.positions || [];
            entry.positions.push(item.position);
          }
        }
      }
    });

    return Array.from(urlMap.values())
      .map(item => ({
        url: item.url,
        pageTitle: item.pageTitle,
        visitors: item.visitors,
        organicVisitors: item.organicVisitors,
        clicks: item.clicks,
        impressions: item.impressions,
        ctr: item.impressions > 0 ? (item.clicks / item.impressions) * 100 : 0,
        avgPosition: item.positions && item.positions.length > 0 
          ? item.positions.reduce((sum: number, pos: number) => sum + pos, 0) / item.positions.length
          : 0,
        bounceRate: item.bounceCount > 0 ? (item.bounceRate / item.bounceCount) * 100 : 0
      }))
      .sort((a, b) => b.visitors - a.visitors) // Sort by most visitors first
      .slice(0, 10);
  };

  const createTopQueriesData = (searchData: any[], keywordData: any[] = []): TopQueryData[] => {
    // Group by query and aggregate metrics - combine data from both tables
    const queryMap = new Map();
    
    // Process search_console_metrics data (may not have query field)
    searchData.forEach(item => {
      // Include all queries with data
      if (item.query) {
        if (!queryMap.has(item.query)) {
          queryMap.set(item.query, {
            query: item.query,
            clicks: 0,
            impressions: 0,
            visitors: 0,
            positions: [] // Store all positions to calculate average
          });
        }
        const entry = queryMap.get(item.query);
        entry.clicks += item.clicks || 0;
        entry.impressions += item.impressions || 0;
        entry.visitors += Math.floor((item.clicks || 0) * 1.2); // Estimate visitors from clicks
        if (item.position) {
          entry.positions.push(item.position);
        }
      }
    });

    // Process search_console_keywords data if available
    keywordData.forEach(item => {
      if (item.query) {
        if (!queryMap.has(item.query)) {
          queryMap.set(item.query, {
            query: item.query,
            clicks: 0,
            impressions: 0,
            visitors: 0,
            positions: []
          });
        }
        const entry = queryMap.get(item.query);
        entry.clicks += item.clicks || 0;
        entry.impressions += item.impressions || 0;
        entry.visitors += Math.floor((item.clicks || 0) * 1.2);
        if (item.position || item.avg_position) {
          entry.positions.push(item.position || item.avg_position);
        }
      }
    });

    return Array.from(queryMap.values())
      .sort((a, b) => b.clicks - a.clicks) // Sort by most clicks first
      .slice(0, 10)
      .map(item => ({
        query: item.query,
        clicks: item.clicks,
        impressions: item.impressions,
        visitors: item.visitors,
        ctr: item.impressions > 0 ? (item.clicks / item.impressions) * 100 : 0,
        position: item.positions.length > 0 
          ? item.positions.reduce((sum: number, pos: number) => sum + pos, 0) / item.positions.length 
          : 0
      }));
  };

  // Chart configurations (much better than OLD static charts)
  const trafficChartData = {
    labels: trafficTrend.map(item => {
      // Handle both YYYY-MM and YYYY-MM-DD formats
      const dateParts = item.date.split('-');
      if (dateParts.length === 2) {
        // Monthly data (YYYY-MM) - convert to proper Date
        const [year, month] = dateParts;
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      } else {
        // Daily data (YYYY-MM-DD)
        return new Date(item.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      }
    }),
    datasets: [
      {
        label: 'Total Visitors',
        data: trafficTrend.map(item => item.visitors),
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Organic Visitors',
        data: trafficTrend.map(item => item.organicVisitors),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const searchChartData = {
    labels: trafficTrend.map(item => {
      // Handle both YYYY-MM and YYYY-MM-DD formats
      const dateParts = item.date.split('-');
      if (dateParts.length === 2) {
        // Monthly data (YYYY-MM) - convert to proper Date
        const [year, month] = dateParts;
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      } else {
        // Daily data (YYYY-MM-DD)
        return new Date(item.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      }
    }),
    datasets: [
      {
        label: 'Clicks',
        data: trafficTrend.map(item => item.clicks),
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Impressions (÷100)',
        data: trafficTrend.map(item => Math.floor(item.impressions / 100)),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Date'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Count'
        },
        beginAtZero: true
      }
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false
    }
  };

  // Utility functions
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatPercentage = (num: number) => {
    return `${num.toFixed(1)}%`;
  };

  const formatDuration = (duration: number) => {
    // Check if the duration is in nanoseconds (very large number)
    if (duration > 1000000000) {
      // Convert nanoseconds to seconds
      duration = duration / 1000000000;
    }
    
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getTrendIcon = (current: number, previous?: number) => {
    if (!previous) return <RemoveIcon sx={{ fontSize: 16, color: 'grey.500' }} />;
    if (current > previous) return <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />;
    if (current < previous) return <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />;
    return <RemoveIcon sx={{ fontSize: 16, color: 'grey.500' }} />;
  };

  // Effects
  useEffect(() => {
    fetchWebsites();
  }, []);

  useEffect(() => {
    if (selectedWebsiteId !== 'all' && websites.length > 0) {
      fetchAnalyticsData();
    }
  }, [selectedWebsiteId, dateFilter, selectedContentType, urlFilter, websites]);
  
  // Fetch filter options when website changes
  useEffect(() => {
    if (selectedWebsiteId !== 'all') {
      fetchFilterOptions();
    }
  }, [selectedWebsiteId]);

  // Don't automatically set mock data - let's see what real data we get
  useEffect(() => {
    if (selectedWebsiteId === 'all') {
      // Reset to empty state instead of showing mock data
      setMetrics({
        totalVisitors: 0,
        organicVisitors: 0,
        totalSessions: 0,
        bounceRate: 0,
        avgSessionDuration: 0,
        newUserPercentage: 0,
        totalClicks: 0,
        totalImpressions: 0,
        avgCTR: 0,
        avgPosition: 0
      });
      setTrafficTrend([]);
      setTopPages([]);
      setTopQueries([]);
    }
  }, []);

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 4 }}>
          Analytics
        </Typography>

        {selectedWebsiteId === 'all' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
              Select a website below to view detailed analytics data
            </Typography>
          </Box>
        )}

        {/* Website Selector */}
        <Box sx={{ mb: 3 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="website-select-label">Select Website</InputLabel>
            <Select
              labelId="website-select-label"
              value={selectedWebsiteId}
              label="Select Website"
              onChange={handleWebsiteChange}
            >
              <MenuItem value="all">All Websites</MenuItem>
              {websites.map((website) => {
                const flagEmojis: { [key: string]: string } = {
                  'UK': '🇬🇧', 'FI': '🇫🇮', 'FR': '🇫🇷', 'JP': '🇯🇵', 'CA': '🇨🇦', 
                  'DE': '🇩🇪', 'ES': '🇪🇸', 'IT': '🇮🇹', 'NL': '🇳🇱', 'AU': '🇦🇺',
                  'BR': '🇧🇷', 'CN': '🇨🇳', 'DK': '🇩🇰', 'KR': '🇰🇷', 'NO': '🇳🇴',
                  'RU': '🇷🇺', 'SE': '🇸🇪', 'COM': '🌐'
                };
                
                const cleanUrl = website.base_url
                  .replace(/^https?:\/\//, '')
                  .replace(/^www\./, '');
                
                return (
                  <MenuItem key={website.website_id} value={website.website_id}>
                    {flagEmojis[website.country] || '🌐'} {cleanUrl}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)', border: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ mr: 1, color: '#666' }} />
            <Typography variant="h6" sx={{ color: '#333', fontWeight: 600, flexGrow: 1 }}>
              Filters & View Options
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Clear
            </Button>
          </Box>
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <DateRangeSelector
                fromDate={dateFilter.fromDate}
                toDate={dateFilter.toDate}
                onChange={handleDateRangeChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Content Type</InputLabel>
                <Select
                  value={selectedContentType}
                  label="Content Type"
                  onChange={(e) => setSelectedContentType(e.target.value)}
                >
                  {contentTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={availableUrls}
                value={urlFilter}
                onChange={(_, newValue) => setUrlFilter(newValue || '')}
                disabled={selectedWebsiteId === 'all'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by URL"
                    size="small"
                    placeholder="Select or type URL..."
                  />
                )}
                freeSolo
                clearOnEscape
                sx={{ 
                  '& .MuiAutocomplete-input': {
                    minWidth: '200px'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode) => newMode && setViewMode(newMode)}
                size="small"
                fullWidth
              >
                <ToggleButton value="combined">Combined</ToggleButton>
                <ToggleButton value="traffic">Traffic</ToggleButton>
                <ToggleButton value="search">Search</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </Grid>
        </Paper>

        {/* Metrics Overview */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Traffic Metrics */}
          {(viewMode === 'combined' || viewMode === 'traffic') && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PeopleIcon sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography color="text.secondary" variant="body2">
                      Total Visitors
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
                    {metrics.totalVisitors === 0 && trafficTrend.length === 0 ? 'No data' : formatNumber(metrics.totalVisitors)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getTrendIcon(metrics.totalVisitors, 42000)}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                      vs last period
                    </Typography>
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SearchIcon sx={{ color: 'success.main', mr: 1 }} />
                    <Typography color="text.secondary" variant="body2">
                      Organic Visitors
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
                    {metrics.organicVisitors === 0 && trafficTrend.length === 0 ? 'No data' : formatNumber(metrics.organicVisitors)}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {formatPercentage((metrics.organicVisitors / metrics.totalVisitors) * 100)} of total
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SpeedIcon sx={{ color: 'warning.main', mr: 1 }} />
                    <Typography color="text.secondary" variant="body2">
                      Bounce Rate
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
                    {metrics.bounceRate === 0 && trafficTrend.length === 0 ? 'No data' : formatPercentage(metrics.bounceRate)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg: {formatDuration(metrics.avgSessionDuration)}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TimelineIcon sx={{ color: 'info.main', mr: 1 }} />
                    <Typography color="text.secondary" variant="body2">
                      New Users
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
                    {formatPercentage(metrics.newUserPercentage)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatNumber(metrics.totalSessions)} sessions
                  </Typography>
                </Card>
              </Grid>
            </>
          )}

          {/* Search Console Metrics */}
          {(viewMode === 'combined' || viewMode === 'search') && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TouchAppIcon sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography color="text.secondary" variant="body2">
                      Total Clicks
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
                    {metrics.totalClicks === 0 && topQueries.length === 0 ? 'No data' : formatNumber(metrics.totalClicks)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getTrendIcon(metrics.totalClicks, 11500)}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                      vs last period
                    </Typography>
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <VisibilityIcon sx={{ color: 'info.main', mr: 1 }} />
                    <Typography color="text.secondary" variant="body2">
                      Impressions
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
                    {metrics.totalImpressions === 0 && topQueries.length === 0 ? 'No data' : formatNumber(metrics.totalImpressions)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    CTR: {formatPercentage(metrics.avgCTR)}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUpIcon sx={{ color: 'success.main', mr: 1 }} />
                    <Typography color="text.secondary" variant="body2">
                      Avg CTR
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
                    {formatPercentage(metrics.avgCTR)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Industry avg: 2.5%
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SearchIcon sx={{ color: 'warning.main', mr: 1 }} />
                    <Typography color="text.secondary" variant="body2">
                      Avg Position
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
                    {metrics.avgPosition.toFixed(1)}
                  </Typography>
                  <Chip 
                    label={metrics.avgPosition <= 10 ? "Top 10" : "Needs Work"} 
                    size="small"
                    color={metrics.avgPosition <= 10 ? "success" : "warning"}
                  />
                </Card>
              </Grid>
            </>
          )}
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {(viewMode === 'combined' || viewMode === 'traffic') && (
            <Grid item xs={12} lg={6}>
              <Card sx={{ p: 3, height: 400 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Traffic Trends
                </Typography>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ height: 300 }}>
                    <Line data={trafficChartData} options={chartOptions} />
                  </Box>
                )}
              </Card>
            </Grid>
          )}

          {(viewMode === 'combined' || viewMode === 'search') && (
            <Grid item xs={12} lg={6}>
              <Card sx={{ p: 3, height: 400 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Search Performance
                </Typography>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ height: 300 }}>
                    <Line data={searchChartData} options={chartOptions} />
                  </Box>
                )}
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Data Tables */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Top Pages
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {topPages.map((page, index) => (
                  <Box key={index} sx={{ py: 2, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      {page.pageTitle}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {cleanUrl(page.url)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Chip label={`${formatNumber(page.visitors)} visitors`} size="small" />
                      <Chip label={`${formatNumber(page.clicks)} clicks`} size="small" color="primary" />
                      <Chip label={`${formatPercentage(page.ctr)} CTR`} size="small" color="success" />
                      <Chip label={`#${page.avgPosition.toFixed(1)} avg pos`} size="small" color="warning" />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Top Search Queries
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {topQueries.map((query, index) => (
                  <Box key={index} sx={{ py: 2, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      {query.query}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Chip label={`${formatNumber(query.clicks)} clicks`} size="small" color="primary" />
                      <Chip label={`${formatNumber(query.impressions)} impressions`} size="small" />
                      <Chip label={`${formatPercentage(query.ctr)} CTR`} size="small" color="success" />
                      <Chip label={`#${query.position.toFixed(1)} position`} size="small" color="warning" />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* No Data Message */}
        {selectedWebsiteId !== 'all' && trafficTrend.length === 0 && topPages.length === 0 && (
          <Box sx={{ mt: 4, p: 3, bgcolor: '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No analytics data available for the selected website and date range.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Please try selecting a different date range or check if data has been imported for this website.
            </Typography>
          </Box>
        )}
        
        {/* Footer Note */}
        <Box sx={{ mt: 4, p: 2, bgcolor: '#fafafa', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            * Analytics data shows aggregated performance metrics for the selected website. 
            Traffic data is updated monthly, while search console data may update more frequently.
            {trafficTrend.length === 0 && ' Values showing "0" indicate actual zero traffic/activity, while "No data" indicates missing data.'}
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default AnalyticsPage;